import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { Eye, EyeOff, Users } from 'lucide-react';
import { ApiError } from '../lib/api.js';
import { useLogin, usePublicConfig, useRegister } from '../lib/auth.js';
import { safeRedirectPath } from '../lib/navigation.js';

function Brand() {
  return (
    <div className="brand-lg">
      <span className="grid size-11 flex-none place-items-center rounded-xl bg-navy text-lg font-extrabold text-white">
        r.
      </span>
      <div>
        <strong>retro.blue</strong>
        <span>Team retrospectives · internal</span>
      </div>
    </div>
  );
}

export function AuthPage({ mode }: { mode: 'login' | 'register' }) {
  const isRegister = mode === 'register';
  const { data: config } = usePublicConfig();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Never trust a redirect target from the query string.
  const next = safeRedirectPath(searchParams.get('next'));
  // Arrived from an invite link. The server decides whether it is real; here it
  // only means "offer the form", since turning an invited colleague away would
  // leave them with nowhere to go.
  const inviteToken = searchParams.get('invite') ?? undefined;
  // Assume open until told otherwise, so a slow request does not flash
  // "invite-only" at someone who is allowed to sign up.
  const registrationOpen = (config?.allowRegistration ?? true) || Boolean(inviteToken);

  const login = useLogin();
  const register = useRegister();
  const pending = login.isPending || register.isPending;

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(event: React.FormEvent) {
    event.preventDefault();
    setError(null);

    try {
      if (isRegister) {
        await register.mutateAsync({ name, email, password, inviteToken });
      } else {
        await login.mutateAsync({ email, password });
      }
      navigate(next, { replace: true });
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Something went wrong. Please try again.');
    }
  }

  return (
    <div className="signin">
      <div className="signin-card">
        <Brand />

        <div>
          <h2 className="text-[22px] font-bold">{isRegister ? 'Create your account' : 'Sign in'}</h2>
          <p className="lede">
            {isRegister
              ? 'Run better retrospectives with your team.'
              : 'Welcome back. Sign in to see your team’s sessions and actions.'}
          </p>
        </div>

        {isRegister && !registrationOpen ? (
          <>
            <div className="notice-neutral">
              <span>
                This instance is invite-only, so accounts are not open to sign-up. Ask a facilitator to
                send you a team invite, or a guest link if you only need to join a single session.
              </span>
            </div>
            <Link to="/login" className="btn btn-primary btn-block">
              Sign in instead
            </Link>
          </>
        ) : (
          <form onSubmit={onSubmit} className="grid gap-3">
            {isRegister ? (
              <label className="field-l">
                Your name
                <input
                  className="input"
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                  required
                  maxLength={60}
                  autoComplete="name"
                  placeholder="Alex Rivera"
                />
              </label>
            ) : null}

            <label className="field-l">
              Work email
              <input
                className="input"
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                required
                autoComplete="email"
                placeholder="you@company.com"
              />
            </label>

            <div className="field-l">
              <label htmlFor="auth-password">Password</label>
              <span className="relative">
                <input
                  id="auth-password"
                  className="input pr-14"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  required
                  minLength={isRegister ? 10 : 1}
                  autoComplete={isRegister ? 'new-password' : 'current-password'}
                  placeholder={isRegister ? 'At least 10 characters' : ''}
                  // Described rather than labelled, so screen readers announce the
                  // field as "Password" and not the whole sentence of advice.
                  aria-describedby="password-hint"
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 flex items-center px-3 text-[12px] font-semibold text-muted hover:text-ink"
                  onClick={() => setShowPassword((show) => !show)}
                  aria-label={showPassword ? 'Hide characters' : 'Show characters'}
                >
                  {showPassword ? <EyeOff size={15} aria-hidden /> : <Eye size={15} aria-hidden />}
                  <span className="ml-1">{showPassword ? 'Hide' : 'Show'}</span>
                </button>
              </span>
            </div>
            {isRegister ? (
              <p id="password-hint" className="hint">
                At least 10 characters. Length beats complexity — a short phrase works well.
              </p>
            ) : (
              <span id="password-hint" className="sr-only">
                Your account password
              </span>
            )}

            {error ? (
              <p role="alert" className="err-msg">
                {error}
              </p>
            ) : null}

            <button type="submit" className="btn btn-primary btn-block btn-lg" disabled={pending}>
              {pending ? 'Just a moment…' : isRegister ? 'Create account' : 'Sign in'}
            </button>
          </form>
        )}

        <div className="notice">
          <Users size={15} className="i mt-0.5 flex-none" aria-hidden />
          <span>
            <strong>Joining a retro as a guest?</strong> Open the invite link from your facilitator.
            You don’t need an account.
          </span>
        </div>

        {!isRegister ? (
          <p className="mt-3 text-center text-xs text-ink/50">
            Forgot your password? Ask an administrator for a reset link.
          </p>
        ) : null}

        <p className="fine text-center">
          {isRegister ? (
            <>
              Already have an account?{' '}
              <Link className="link" to="/login">
                Sign in
              </Link>
            </>
          ) : registrationOpen ? (
            <>
              No account yet?{' '}
              <Link className="link" to="/register">
                Create one
              </Link>
            </>
          ) : (
            // No sign-up link on a closed instance: joining happens by invitation.
            <>No account yet? Ask a team facilitator to add you.</>
          )}
        </p>
      </div>
    </div>
  );
}
